import { HIERARCHY_SEPARATOR } from '@/shared/lib/format/separators';
import { cn } from '@/shared/lib/utils';
import { Text } from '@/shared/ui';
import { Checkbox } from '@/shared/ui/checkbox/Checkbox';
import { Tooltip } from '@/shared/ui/tooltip/Tooltip';
import type { SearchResult } from '../utils/search-utils';

type CascaderSearchResultItemProps = {
  result: SearchResult;
  index: number;
  isSelected: boolean;
  isSelectDisabled: boolean;
  isActive: boolean;
  showCheckbox: boolean;
  multiple: boolean;
  id?: string;
  onSelect: () => void;
};

export const CascaderSearchResultItem = ({
  result,
  index,
  isSelected,
  isSelectDisabled,
  isActive,
  showCheckbox,
  multiple,
  id,
  onSelect,
}: CascaderSearchResultItemProps) => {
  const pathText = result.pathLabels.join(HIERARCHY_SEPARATOR);

  const content = (
    <div
      role="option"
      id={id ? `${id}-search-option-${index}` : undefined}
      aria-selected={isSelected}
      aria-disabled={isSelectDisabled}
      tabIndex={-1}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      onMouseDown={(e) => e.preventDefault()}
      className={cn(
        'flex w-full cursor-pointer items-start gap-2 px-3 py-2 text-left text-sm',
        'hover:bg-brand-100',
        isActive && 'bg-surface-accent',
        isSelectDisabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {multiple && showCheckbox && (
        <Checkbox
          checked={isSelected}
          className="pointer-events-none mt-0.5"
          disabled={isSelectDisabled}
        />
      )}
      <div className="flex flex-col">
        <Text as="span" className="font-medium leading-none">
          {result.option.label}
        </Text>
        <Text as="span" variant="description" color="muted">
          {pathText}
        </Text>
      </div>
    </div>
  );

  return (
    <Tooltip
      tooltipContent={result.option.disabledReason}
      enabled={Boolean(isSelectDisabled && result.option.disabledReason)}
      side="right"
    >
      {content}
    </Tooltip>
  );
};
